import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "@amzn/awsui-components-react/polaris";

import { RootState } from "../../app/rootReducer";
import {
  ERROR_NOTIF_ITEMS,
  WARNING_NOTIF_ITEMS,
} from "../../notification/notification-config";
import { setNotification } from "../../notification/notificationConnector";
import { trackButtonClick } from "../../telemetry/telemetry";
import { exportCSV, getTimestampedCsvFileName } from "../../../util/fileUtils";
import { buildValidationExportCsv } from "./buildValidationExportCsv";
import { fetchAllPagesForCurrentQuery } from "./fetchAllPagesForCurrentQuery";
import { formatMatchCount } from "./formatMatchCount";

export interface ExportCsvButtonProps {
  readonly maxRows?: number;
  readonly timeoutMs?: number;
}

const DEFAULT_MAX_ROWS = 10_000;
const DEFAULT_TIMEOUT_MS = 30_000;
// Keep the historical filename prefix so users who script against the
// download filename (or just recognize it visually) aren't disrupted.
const FILE_NAME_PREFIX = "porExport";
const ARIA_LABEL = "Export CSV";
const DISABLED_REASON_ZERO = "No rows to export";
const ERROR_MESSAGE = "Failed to export CSV";

function truncationMessage(exportedRows: number): string {
  return `Only the first ${formatMatchCount(
    exportedRows
  )} rows were exported. Narrow the filter to export the remaining rows.`;
}

type CopyState = { readonly kind: "idle" } | { readonly kind: "in-flight" };

export const ExportCsvButton: React.FC<ExportCsvButtonProps> = ({
  maxRows = DEFAULT_MAX_ROWS,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}) => {
  const dispatch = useDispatch();
  const count = useSelector((state: RootState) => state.porAsinList.count);
  const queryString = useSelector(
    (state: RootState) => state.generateQuery.queryString
  );

  const [state, setState] = useState<CopyState>({ kind: "idle" });
  const controllerRef = useRef<AbortController | null>(null);
  const mountedRef = useRef<boolean>(true);

  const abortInFlight = useCallback(() => {
    if (controllerRef.current !== null) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      abortInFlight();
    };
  }, [abortInFlight, queryString]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const zeroCount = count === 0;
  const isInFlight = state.kind === "in-flight";
  const disabled = zeroCount;
  const disabledReason = zeroCount ? DISABLED_REASON_ZERO : undefined;

  const handleClick = useCallback(async () => {
    if (zeroCount || isInFlight) {
      return;
    }

    trackButtonClick("validationDashboardTable", "ExportCsv", {
      matchCount: count,
    });

    const controller = new AbortController();
    controllerRef.current = controller;

    setState({ kind: "in-flight" });

    try {
      const { rows, truncated } = await fetchAllPagesForCurrentQuery({
        queryString,
        signal: controller.signal,
        maxAsins: maxRows,
        timeoutMs,
        // Mirror the legacy porExport, which surfaced restricted rows
        // with a "Restricted Access (...)" program label. The bulk
        // submit / copy flows use the default (restricted excluded).
        includeRestricted: true,
      });

      controllerRef.current = null;

      if (!mountedRef.current) {
        return;
      }

      const csvData = buildValidationExportCsv(rows);
      const fileName = getTimestampedCsvFileName(FILE_NAME_PREFIX);

      exportCSV({
        fileName,
        csvData: csvData as unknown as [[]],
      });

      if (truncated) {
        dispatch(
          setNotification(WARNING_NOTIF_ITEMS(truncationMessage(rows.length)))
        );
      }

      if (mountedRef.current) {
        setState({ kind: "idle" });
      }
    } catch {
      controllerRef.current = null;

      // The fetcher owns the timeout. If the caller-side controller
      // was aborted, treat as silent (unmount / queryString change).
      // Everything else — timeout, network error — surfaces the Flashbar.
      const silentAbort = controller.signal.aborted;
      if (!silentAbort) {
        dispatch(setNotification(ERROR_NOTIF_ITEMS(ERROR_MESSAGE)));
      }
      if (mountedRef.current) {
        setState({ kind: "idle" });
      }
    }
  }, [count, dispatch, isInFlight, maxRows, queryString, timeoutMs, zeroCount]);

  return (
    <Button
      data-testid="export-csv-button"
      variant="normal"
      iconName="download"
      ariaLabel={ARIA_LABEL}
      disabled={disabled}
      disabledReason={disabledReason}
      loading={isInFlight}
      onClick={handleClick}
    >
      Export
    </Button>
  );
};

export default ExportCsvButton;
